import React, { createContext, useContext, useEffect, useState } from 'react';

import { apiService } from '../services/apiServices';
import { useUserRole } from '../hooks/useUserRole';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PermissionEntry {
  key: string;
  name: string;
  allowed: boolean;
}

interface PrivilegeEntry {
  key: string;
  name: string;
  permissions: PermissionEntry[];
}

interface UserPermissionsData {
  userId: string;
  privileges: PrivilegeEntry[];
}

export interface UserPrivileges {
  canListFacilities: boolean;
  canCreateFacility: boolean;
  canEditFacility: boolean;
  canListFacilityUser: boolean;
  canCreateFacilityUser: boolean;
  canEditFacilityUser: boolean;
  canCreateEquipment: boolean;
  canEditEquipment: boolean;
  canListEquipment: boolean;
  canToggleEmployeeStatus: boolean;
  canCreateContactRequest: boolean;
  canCreateServiceRequest: boolean;
  canCancelServiceRequest: boolean;
  /** TECHNICIAN only — whether the technician can accept or reject an assigned task. */
  canAcceptRejectTask: boolean;
  /** TECHNICIAN only — whether the technician can reschedule/reassign an assigned task. */
  canRescheduleReassignTask: boolean;
  /** TECHNICIAN-only — gates the "Update Task Status" action (RR-624). */
  canUpdateTaskStatus: boolean;
  /** TECHNICIAN only — whether the technician can view assigned task (SR) details. */
  canViewTask: boolean;
  /** TECHNICIAN only — whether the technician can fill a task form. */
  canFillTaskForm: boolean;
  /** TECHNICIAN only — whether the technician can list their submitted forms. */
  canListSubmittedForms: boolean;
  /** TECHNICIAN only — whether the technician can view a submitted form detail. */
  canViewSubmittedForms: boolean;
  /** CLIENT / TECHNICIAN — whether the user can list certificates. */
  canListCertificates: boolean;
  /** TECHNICIAN — whether the user can view a generated certificate's detail. */
  canViewCertificate: boolean;
  /** TECHNICIAN — whether the user can download a generated certificate PDF. */
  canDownloadCertificate: boolean;

  // ─── ADMIN privileges (present only when role is ADMIN) ───────────────────
  // Admin User Management
  adminUserCreate?: boolean;
  adminUserEdit?: boolean;
  adminUserList?: boolean;
  adminUserView?: boolean;
  adminUserActivateDeactivate?: boolean;
  // Subscription Management
  subscriptionCreate?: boolean;
  subscriptionEdit?: boolean;
  subscriptionList?: boolean;
  subscriptionView?: boolean;
  subscriptionActivateDeactivate?: boolean;
  // Offer Management
  offerCreate?: boolean;
  offerEdit?: boolean;
  offerList?: boolean;
  offerView?: boolean;
  offerActivateDeactivate?: boolean;
  // Client Management
  clientCreate?: boolean;
  clientEdit?: boolean;
  clientList?: boolean;
  clientView?: boolean;
  clientActivateDeactivate?: boolean;
  // Service Request Management
  serviceRequestList?: boolean;
  serviceRequestView?: boolean;
  serviceRequestAssign?: boolean;
  serviceRequestUpdateStatus?: boolean;
  serviceRequestReschedule?: boolean;
  serviceRequestAcceptReject?: boolean;
  // Contact Request Management
  contactRequestList?: boolean;
  contactRequestView?: boolean;
  contactRequestAssign?: boolean;
  contactRequestReschedule?: boolean;
  contactRequestUpdateStatus?: boolean;
  contactRequestAcceptReject?: boolean;
  // Service Type Management
  serviceTypeCreate?: boolean;
  serviceTypeEdit?: boolean;
  serviceTypeList?: boolean;
  serviceTypeView?: boolean;
  serviceTypeActivateDeactivate?: boolean;
  // Technician Management
  technicianCreate?: boolean;
  technicianEdit?: boolean;
  technicianList?: boolean;
  technicianView?: boolean;
  technicianActivateDeactivate?: boolean;
  // QMS Document Category Management
  qmsDocCategoryCreate?: boolean;
  qmsDocCategoryEdit?: boolean;
  qmsDocCategoryList?: boolean;
  qmsDocCategoryView?: boolean;
  qmsDocCategoryActivateDeactivate?: boolean;
  // Certificate Template Management
  certificateTemplateCreate?: boolean;
  certificateTemplateEdit?: boolean;
  certificateTemplateList?: boolean;
  certificateTemplateView?: boolean;
  // Certificate Management (admin view)
  certificateList?: boolean;
  certificateView?: boolean;
  certificateDownload?: boolean;
  // Other
  dashboardView?: boolean;
  exportReports?: boolean;
}

export interface UseUserPrivilegesResult {
  privileges: UserPrivileges;
  isLoading: boolean;
  error: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const PERMISSIONS_ENDPOINT = 'policy-management/permissions';

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_PRIVILEGES: UserPrivileges = {
  canListFacilities: false,
  canCreateFacility: false,
  canEditFacility: false,
  canListFacilityUser: false,
  canCreateFacilityUser: false,
  canEditFacilityUser: false,
  canCreateEquipment: false,
  canEditEquipment: false,
  canListEquipment: false,
  canToggleEmployeeStatus: false,
  canCreateContactRequest: false,
  canCreateServiceRequest: false,
  canCancelServiceRequest: false,
  canAcceptRejectTask: false,
  canRescheduleReassignTask: false,
  canUpdateTaskStatus: false,
  canViewTask: false,
  canFillTaskForm: false,
  canListSubmittedForms: false,
  canViewSubmittedForms: false,
  canListCertificates: false,
  canViewCertificate: false,
  canDownloadCertificate: false,
  // ADMIN privileges default to false
  adminUserCreate: false,
  adminUserEdit: false,
  adminUserList: false,
  adminUserView: false,
  adminUserActivateDeactivate: false,
  subscriptionCreate: false,
  subscriptionEdit: false,
  subscriptionList: false,
  subscriptionView: false,
  subscriptionActivateDeactivate: false,
  offerCreate: false,
  offerEdit: false,
  offerList: false,
  offerView: false,
  offerActivateDeactivate: false,
  clientCreate: false,
  clientEdit: false,
  clientList: false,
  clientView: false,
  clientActivateDeactivate: false,
  serviceRequestList: false,
  serviceRequestView: false,
  serviceRequestAssign: false,
  serviceRequestUpdateStatus: false,
  serviceRequestReschedule: false,
  serviceRequestAcceptReject: false,
  contactRequestList: false,
  contactRequestView: false,
  contactRequestAssign: false,
  contactRequestReschedule: false,
  contactRequestUpdateStatus: false,
  contactRequestAcceptReject: false,
  serviceTypeCreate: false,
  serviceTypeEdit: false,
  serviceTypeList: false,
  serviceTypeView: false,
  serviceTypeActivateDeactivate: false,
  technicianCreate: false,
  technicianEdit: false,
  technicianList: false,
  technicianView: false,
  technicianActivateDeactivate: false,
  qmsDocCategoryCreate: false,
  qmsDocCategoryEdit: false,
  qmsDocCategoryList: false,
  qmsDocCategoryView: false,
  qmsDocCategoryActivateDeactivate: false,
  certificateTemplateCreate: false,
  certificateTemplateEdit: false,
  certificateTemplateList: false,
  certificateTemplateView: false,
  certificateList: false,
  certificateView: false,
  certificateDownload: false,
  dashboardView: false,
  exportReports: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const parsePrivileges = (
  privileges: PrivilegeEntry[]
): UserPrivileges => {
  const find = (groupKey: string | string[], permKey: string): boolean => {
    const keys = Array.isArray(groupKey) ? groupKey : [groupKey];
    for (const key of keys) {
      const group = privileges.find((p) => p.key === key);
      const perm = group?.permissions.find((p) => p.key === permKey);
      if (perm !== undefined) {
        return perm.allowed;
      }
    }
    return false;
  };
  const allPermissions = privileges.flatMap((p) => p.permissions);
  const taskPrivilege = privileges.find(
    (p) => p.key === 'manage_assigned_tasks'
  );
  return {
    canListFacilities: find('manage_facility', 'facilityList'),
    canCreateFacility: find('manage_facility', 'facilityCreate'),
    canEditFacility: find('manage_facility', 'facilityEdit'),
    canListFacilityUser: find('manage_facility_user', 'facilityUserList'),
    canCreateFacilityUser: find('manage_facility_user', 'facilityUserCreate'),
    canEditFacilityUser: find('manage_facility_user', 'facilityUserEdit'),
    canCreateEquipment: find('manage_equipment', 'equipmentCreate'),
    canEditEquipment: find('manage_equipment', 'equipmentEdit'),
    canListEquipment: find('manage_equipment', 'equipmentList'),
    canToggleEmployeeStatus: find(
      'manage_facility_user',
      'facilityUserActivateDeactivate'
    ),
    canCreateContactRequest:
      allPermissions.find((p) => p.key === 'contactRequestCreate')?.allowed ??
      false,
    canCreateServiceRequest: find(
      'manage_service_request',
      'serviceRequestCreate'
    ),
    canCancelServiceRequest: find(
      'manage_service_request',
      'serviceRequestCancel'
    ),
    canAcceptRejectTask: find('manage_assigned_tasks', 'taskAcceptReject'),
    canRescheduleReassignTask: find(
      'manage_assigned_tasks',
      'taskRescheduleReassign'
    ),
    canUpdateTaskStatus: find('manage_assigned_tasks', 'taskUpdateStatus'),
    canViewTask: find('manage_assigned_tasks', 'taskView'),
    canFillTaskForm: find('manage_assigned_tasks', 'taskFillForm'),
    canListSubmittedForms:
      taskPrivilege?.permissions.find((p) => p.key === 'submittedTaskFormList')
        ?.allowed ?? true,
    canViewSubmittedForms:
      taskPrivilege?.permissions.find((p) => p.key === 'submittedTaskFormView')
        ?.allowed ?? true,
    canListCertificates: find(
      ['certificate_management', 'manage_certificate'],
      'certificateList'
    ),
    canViewCertificate: find(
      ['certificate_management', 'manage_certificate'],
      'certificateView'
    ),
    canDownloadCertificate: find(
      ['certificate_management', 'manage_certificate'],
      'certificateDownload'
    ),
    // ADMIN privileges
    adminUserCreate: find('admin_user_management', 'adminUserCreate'),
    adminUserEdit: find('admin_user_management', 'adminUserEdit'),
    adminUserList: find('admin_user_management', 'adminUserList'),
    adminUserView: find('admin_user_management', 'adminUserView'),
    adminUserActivateDeactivate: find(
      'admin_user_management',
      'adminUserActivateDeactivate'
    ),
    subscriptionCreate: find('subscription_management', 'subscriptionCreate'),
    subscriptionEdit: find('subscription_management', 'subscriptionEdit'),
    subscriptionList: find('subscription_management', 'subscriptionList'),
    subscriptionView: find('subscription_management', 'subscriptionView'),
    subscriptionActivateDeactivate: find(
      'subscription_management',
      'subscriptionActivateDeactivate'
    ),
    offerCreate: find('offer_management', 'offerCreate'),
    offerEdit: find('offer_management', 'offerEdit'),
    offerList: find('offer_management', 'offerList'),
    offerView: find('offer_management', 'offerView'),
    offerActivateDeactivate: find(
      'offer_management',
      'offerActivateDeactivate'
    ),
    clientCreate: find('client_management', 'clientCreate'),
    clientEdit: find('client_management', 'clientUpdate'),
    clientList: find('client_management', 'clientList'),
    clientView: find('client_management', 'clientView'),
    clientActivateDeactivate: find(
      'client_management',
      'clientActivateDeactivate'
    ),
    serviceRequestList: find(
      'service_request_management',
      'serviceRequestList'
    ),
    serviceRequestView: find(
      'service_request_management',
      'serviceRequestView'
    ),
    serviceRequestAssign: find(
      'service_request_management',
      'serviceRequestAssign'
    ),
    serviceRequestUpdateStatus: find(
      'service_request_management',
      'serviceRequestUpdateStatus'
    ),
    serviceRequestReschedule: find(
      'service_request_management',
      'serviceRequestReschedule'
    ),
    serviceRequestAcceptReject: find(
      'service_request_management',
      'serviceRequestAcceptReject'
    ),
    contactRequestList: find(
      'contact_request_management',
      'contactRequestList'
    ),
    contactRequestView: find(
      'contact_request_management',
      'contactRequestView'
    ),
    contactRequestAssign: find(
      'contact_request_management',
      'contactRequestAssign'
    ),
    contactRequestReschedule: find(
      'contact_request_management',
      'contactRequestReschedule'
    ),
    contactRequestUpdateStatus: find(
      'contact_request_management',
      'contactRequestUpdateStatus'
    ),
    contactRequestAcceptReject: find(
      'contact_request_management',
      'contactRequestAcceptReject'
    ),
    serviceTypeCreate: find('service_type_management', 'serviceTypeCreate'),
    serviceTypeEdit: find('service_type_management', 'serviceTypeEdit'),
    serviceTypeList: find('service_type_management', 'serviceTypeList'),
    serviceTypeView: find('service_type_management', 'serviceTypeView'),
    serviceTypeActivateDeactivate: find(
      'service_type_management',
      'serviceTypeActivateDeactivate'
    ),
    technicianCreate: find('technician_management', 'technicianCreate'),
    technicianEdit: find('technician_management', 'technicianEdit'),
    technicianList: find('technician_management', 'technicianList'),
    technicianView: find('technician_management', 'technicianView'),
    technicianActivateDeactivate: find(
      'technician_management',
      'technicianActivateDeactivate'
    ),
    qmsDocCategoryCreate: find(
      'qms_doc_category_management',
      'qmsDocCategoryCreate'
    ),
    qmsDocCategoryEdit: find(
      'qms_doc_category_management',
      'qmsDocCategoryEdit'
    ),
    qmsDocCategoryList: find(
      'qms_doc_category_management',
      'qmsDocCategoryList'
    ),
    qmsDocCategoryView: find(
      'qms_doc_category_management',
      'qmsDocCategoryView'
    ),
    qmsDocCategoryActivateDeactivate: find(
      'qms_doc_category_management',
      'qmsDocCategoryActivateDeactivate'
    ),
    certificateTemplateCreate: find(
      'certificate_template_management',
      'certificateTemplateCreate'
    ),
    certificateTemplateEdit: find(
      'certificate_template_management',
      'certificateTemplateEdit'
    ),
    certificateTemplateList: find(
      'certificate_template_management',
      'certificateTemplateList'
    ),
    certificateTemplateView: find(
      'certificate_template_management',
      'certificateTemplateView'
    ),
    certificateList: find('certificate_management', 'certificateList'),
    certificateView: find('certificate_management', 'certificateView'),
    certificateDownload: find('certificate_management', 'certificateDownload'),
    dashboardView: find('other', 'dashboardView'),
    exportReports: find('other', 'exportReports'),
  };
};

// ─── Context ──────────────────────────────────────────────────────────────────

const UserPrivilegesContext = createContext<UseUserPrivilegesResult>({
  privileges: DEFAULT_PRIVILEGES,
  isLoading: true,
  error: null,
});

/**
 * Fetches `policy-management/permissions` once when the authenticated layout
 * mounts and shares the result with all child components. Prevents a separate
 * permissions API call on every sidenav page navigation.
 *
 * - SUPER_ADMIN receives all-true privileges immediately (no API call).
 * - CLIENT, TECHNICIAN, and ADMIN roles fetch from the permissions endpoint.
 * - Other roles resolve immediately with all-false defaults.
 */
export const UserPrivilegesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    isClient,
    isTechnician,
    isAdmin,
    role,
    isLoading: roleLoading,
  } = useUserRole();
  const [privileges, setPrivileges] =
    useState<UserPrivileges>(DEFAULT_PRIVILEGES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (roleLoading) {
      return;
    }

    if (role === 'SUPER_ADMIN') {
      const allTrue: UserPrivileges = { ...DEFAULT_PRIVILEGES };
      (Object.keys(allTrue) as Array<keyof UserPrivileges>).forEach((key) => {
        (allTrue as unknown as Record<string, boolean>)[key] = true;
      });
      setPrivileges(allTrue);
      setIsLoading(false);
      return;
    }

    if (!isClient && !isTechnician && !isAdmin) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const fetchPrivileges = async (): Promise<void> => {
      try {
        const result = await apiService.getAPI(PERMISSIONS_ENDPOINT);
        if (!mounted) {
          return;
        }
        if (result.error) {
          setError('Nie udało się pobrać uprawnień.');
          setIsLoading(false);
          return;
        }
        const data = result.data as { data: UserPermissionsData } | undefined;
        if (data?.data?.privileges) {
          setPrivileges(parsePrivileges(data.data.privileges));
        }
        setIsLoading(false);
      } catch {
        if (mounted) {
          setError('Nie udało się pobrać uprawnień.');
          setIsLoading(false);
        }
      }
    };

    void fetchPrivileges();

    return () => {
      mounted = false;
    };
  }, [isClient, isTechnician, isAdmin, role, roleLoading]);

  return (
    <UserPrivilegesContext.Provider value={{ privileges, isLoading, error }}>
      {children}
    </UserPrivilegesContext.Provider>
  );
};

/** Reads shared user privileges from context — never triggers an API call. */
// eslint-disable-next-line react-refresh/only-export-components
export const useUserPrivilegesContext = (): UseUserPrivilegesResult =>
  useContext(UserPrivilegesContext);
